import { hideLogoutInterstitial, showLogoutInterstitial } from "./auth-ui";

// ─── Gigya account promise ───────────────────────────────────────────────────
// Fired once on script execution — before DOMContentLoaded, before any React
// component mounts. Every consumer awaits this shared promise instead of
// making its own getAccountInfo call.

const getGigyaAccount = () =>
  new Promise((resolve, reject) => {
    window.gigya.accounts.getAccountInfo({
      callback: (res) => {
        if (!res.UID) { reject(new Error("No active Gigya session")); return; }
        resolve(res);
      },
    });
  });

window.akc.gigyaAccount = getGigyaAccount().catch(() => null);

// ─── Multipass ──────────────────────────────────────────────────────────────
// Requests a multipass token from the server and immediately redirects to the
// Shopify authUrl to establish a session cookie. The token is never stored —
// it is single-use and valid for 15 minutes only.

const requestMultipass = (res, returnTo) => {
  const data = {
    id: res.UID,
    email: res.profile.email,
    firstName: res.profile.firstName,
    lastName: res.profile.lastName,
    returnMultipassToken: true,
    ...(returnTo && { returnTo }),
  };

  return fetch(window.akc.services.multipassUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }).then((res) => {
    if (!res.ok) throw new Error(`Multipass request failed: ${res.status}`);
    return res.json();
  });
};

const doMultipass = (res) => {
  const urlObj = new URL(window.location.href);
  const returnUrl = urlObj.searchParams.get("return_url");
  const returnTo = returnUrl
    ? returnUrl + (urlObj.hash || "")
    : window.loginReturnTo || null;

  requestMultipass(res, returnTo)
    .then((data) => { window.location.href = data.authUrl; })
    .catch((err) => console.error("Multipass redirect failed:", err));
};

// Explicitly log into Shopify via multipass from an active Gigya session.
// Used when the Gigya session is live but the Shopify customer session is missing.
window.akc.loginToShopify = (returnTo) =>
  window.akc.gigyaAccount
    .then((res) => {
      if (!res) throw new Error("No active Gigya session");
      return requestMultipass(res, returnTo);
    })
    .then((data) => { window.location.href = data.authUrl; });

// ─── Session sync ───────────────────────────────────────────────────────────
// On every page load, reconcile Gigya and Shopify session state:
//   Gigya ✓  Shopify ✓  → already in sync, nothing to do
//   Gigya ✓  Shopify ✗  → use multipass to establish Shopify session
//   Gigya ✗  Shopify ✓  → stale Shopify cookie, force logout
//   Gigya ✗  Shopify ✗  → not logged in, nothing to do

const syncAuthState = async () => {
  const shopifyPresent = window.shopifyCustomerSignedIn;
  const gigyaSession = await window.akc.gigyaAccount;

  if (gigyaSession && shopifyPresent) return;

  if (gigyaSession && !shopifyPresent) {
    doMultipass(gigyaSession);
    return;
  }

  if (!gigyaSession && shopifyPresent) {
    window.akc.logout();
  }
};

document.addEventListener("DOMContentLoaded", syncAuthState);

// ─── Gigya event handlers ────────────────────────────────────────────────────
// onLogin: user just logged in — reset the shared promise with the fresh
// session then redirect through multipass to establish the Shopify session.
// onLogout: clear state and redirect to the logout URL.

gigya.accounts.addEventHandlers({
  onLogin: (res) => {
    // Reset the shared promise so post-login consumers get the new session.
    window.akc.gigyaAccount = Promise.resolve(res);
    doMultipass(res);
  },
  onLogout: () => {
    window.akc.gigyaAccount = Promise.resolve(null);
    window.location.href = window.akc.routes.accountLogoutUrl;
  },
});

// ─── Recaptcha ───────────────────────────────────────────────────────────────

const verify = async (token) => {
  const response = await fetch(window.akc.services.recaptchaUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) throw new Error(`Recaptcha verify failed: ${response.status}`);
  const data = await response.json();
  return data.siteVerifyResponse.score <= window.akc.recaptcha.threshold;
};

const checkBot = async (event) => {
  const actions = {
    "gigya-register-form": "sign_up",
    "gigya-login-form": "sign_in",
  };
  const action = actions[event.form];
  if (!action) return;

  const isBot = await new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(window.akc.recaptcha.siteKey, { action })
        .then((token) => verify(token).then(resolve))
        .catch(reject);
    });
  });

  if (isBot) return { form: `Bot detected: prevent ${action.replace("_", " ")}` };
};

// ─── Public API ──────────────────────────────────────────────────────────────

window.akc.getRegisterScreenset = (options) => {
  window.loginReturnTo = options?.returnTo || window.location.href;
  window.gigya.accounts.showScreenSet({
    screenSet: "Login-web-2",
    startScreen: "gigya-register-screen",
    mobileScreenSet: "Login-web-2",
    onBeforeValidation: checkBot,
  });
};

window.akc.getLoginScreenset = (options) => {
  window.loginReturnTo = window.location.pathname === window.akc.routes.cartUrl
    ? window.akc.routes.cartUrl
    : options?.returnTo || window.location.href;

  window.gigya.accounts.showScreenSet({
    screenSet: "Login-web-2",
    startScreen: "gigya-login-screen",
    mobileScreenSet: "Login-web-2",
    onBeforeValidation: checkBot,
  });
};

window.akc.logout = () => {
  const currentPageURL = window.location.href;
  window.gigya.accounts.logout({
    callback: (response) => {
      if (response.errorCode !== 0) {
        console.error("Logout error:", response.errorMessage);
        return;
      }
      showLogoutInterstitial();
      setTimeout(() => {
        window.location.href = currentPageURL;
        hideLogoutInterstitial();
      }, 1000);
    },
  });
};